import ExcelJS from "exceljs";

const formatDateCell = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const MAX_ODO_DELTA_PER_DAY = 2000;
const ODO_FLAG_LABELS = {
  normal: "Normal",
  odo_rusak: "Odo Rusak",
  mt_off: "MT Off",
};
const buildKoreksiOdoHariIniNotice = (nopol) => (
  `ODO yang telah dikoreksi hari ini tidak dapat diupdate melalui menu bulk, kosongkan/skip input odo untuk MT ${String(nopol || "-").trim() || "-"}`
);

const parseJsonObject = (value) => {
  if (!value) return {};
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  return typeof value === "object" && !Array.isArray(value) ? value : {};
};

const getOdoFlagDefault = (reffRaw) => {
  const reff = parseJsonObject(reffRaw);
  const kondisi = String(reff?.flag?.kondisi || "").trim().toLowerCase();
  if (kondisi === "normal" || kondisi === "odo_rusak" || kondisi === "mt_off") {
    return {
      jenis_transaksi: kondisi,
      catatan_flag: String(reff?.flag?.catatan || "").trim(),
    };
  }
  return { jenis_transaksi: "normal", catatan_flag: "" };
};

const hasReffHistory = (reffRaw, key) => {
  const reff = parseJsonObject(reffRaw);
  const value = reff?.[key];
  return Array.isArray(value) ? value.length > 0 : Boolean(value);
};

const getDaySpanAtLeastOne = (fromDate, toDate) => {
  const fromKey = formatDateCell(fromDate);
  const toKey = formatDateCell(toDate);
  if (!fromKey || !toKey) return 1;
  const fromObj = new Date(`${fromKey}T00:00:00`);
  const toObj = new Date(`${toKey}T00:00:00`);
  const diffMs = toObj.getTime() - fromObj.getTime();
  if (!Number.isFinite(diffMs)) return 1;
  const diffDays = Math.floor(diffMs / 86400000);
  return Math.max(1, diffDays);
};

const parseNumeric = (value) => {
  if (value == null) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const normalized = String(value).trim().replace(/\./g, "").replace(",", ".");
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatKapasitasCell = (value) => {
  if (value == null) return "";
  const asNumber = parseNumeric(value);
  if (asNumber != null) return asNumber;
  return String(value).trim();
};

const compareNopolAsc = (a, b) => {
  const nopolA = String(a?.nopol || "");
  const nopolB = String(b?.nopol || "");
  if (nopolA !== nopolB) return nopolA.localeCompare(nopolB);
  return String(a?.id_mt || "").localeCompare(String(b?.id_mt || ""));
};

export async function formBulkOdo({ rows = [], namaCabang = "", tanggal = "" }) {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Bulk Update ODO");
  const tanggalLabel = String(tanggal || "").trim() || formatDateCell(new Date());
  const kmOdoHeader = `km odo tgl. ${tanggalLabel}`;

  worksheet.columns = [
    { header: "id_mt", key: "id_mt", width: 18 },
    { header: "nopol", key: "nopol", width: 18 },
    { header: "jumlah roda", key: "jumlah_roda", width: 14 },
    { header: "kapasitas", key: "kapasitas", width: 14 },
    { header: "last odo", key: "last_odo", width: 14 },
    { header: "tanggal update odo", key: "tanggal_update_odo", width: 18 },
    { header: kmOdoHeader, key: "km_odo_tanggal", width: 24 },
    { header: "jenis transaksi", key: "jenis_transaksi", width: 18 },
    { header: "nama driver", key: "nama_driver", width: 24 },
    { header: "catatan", key: "catatan_flag", width: 28 },
    { header: "keterangan", key: "keterangan", width: 90 },
  ];

  worksheet.views = [{ state: "frozen", ySplit: 1 }];

  const headerRow = worksheet.getRow(1);
  headerRow.font = { bold: true };
  headerRow.alignment = { vertical: "middle", horizontal: "center" };
  headerRow.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFFFF2CC" },
  };
  headerRow.eachCell((cell) => {
    cell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
  });

  const safeRows = (Array.isArray(rows) ? rows : []).slice().sort(compareNopolAsc);
  safeRows.forEach((row) => {
    const flagDefault = getOdoFlagDefault(row?.reff);
    const isKoreksiOdoHariIni = Boolean(Number(row?.has_koreksi_odo_hari_ini || 0))
      || (
        formatDateCell(row?.tanggal_update_odo) === tanggalLabel
        && hasReffHistory(row?.reff, "koreksi_odo")
      );
    worksheet.addRow({
      id_mt: String(row?.id_mt || "").trim(),
      nopol: String(row?.nopol || "").trim(),
      jumlah_roda: Number(row?.jumlah_roda || 0),
      kapasitas: formatKapasitasCell(row?.kapasitas),
      last_odo: Number(row?.last_odo || 0),
      tanggal_update_odo: formatDateCell(row?.tanggal_update_odo),
      km_odo_tanggal: "",
      jenis_transaksi: flagDefault.jenis_transaksi,
      nama_driver: "",
      catatan_flag: flagDefault.catatan_flag,
      keterangan: isKoreksiOdoHariIni ? buildKoreksiOdoHariIniNotice(row?.nopol) : "",
    });
  });

  const startRow = 2;
  const endRow = Math.max(1001, safeRows.length + 1);

  for (let rowIdx = startRow; rowIdx <= endRow; rowIdx += 1) {
    const idCellValue = String(worksheet.getCell(`A${rowIdx}`).value || "").trim();
    const hasData = Boolean(idCellValue);

    worksheet.getCell(`A${rowIdx}`).protection = { locked: true };
    worksheet.getCell(`B${rowIdx}`).protection = { locked: true };
    worksheet.getCell(`C${rowIdx}`).protection = { locked: true };
    worksheet.getCell(`D${rowIdx}`).protection = { locked: true };
    worksheet.getCell(`E${rowIdx}`).protection = { locked: true };
    worksheet.getCell(`F${rowIdx}`).protection = { locked: true };
    worksheet.getCell(`G${rowIdx}`).protection = { locked: false };
    worksheet.getCell(`H${rowIdx}`).protection = { locked: false };
    worksheet.getCell(`I${rowIdx}`).protection = { locked: false };
    worksheet.getCell(`J${rowIdx}`).protection = { locked: false };
    worksheet.getCell(`K${rowIdx}`).protection = { locked: true };

    worksheet.getCell(`C${rowIdx}`).numFmt = "0";
    const kapasitasCell = worksheet.getCell(`D${rowIdx}`);
    if (typeof kapasitasCell.value === "number") {
      kapasitasCell.numFmt = "#,##0.##";
    }
    worksheet.getCell(`E${rowIdx}`).numFmt = "#,##0";
    worksheet.getCell(`G${rowIdx}`).numFmt = "#,##0";
    worksheet.getCell(`K${rowIdx}`).alignment = { wrapText: true, vertical: "middle" };

    if (!hasData) {
      worksheet.getCell(`G${rowIdx}`).dataValidation = {
        type: "decimal",
        operator: "greaterThanOrEqual",
        allowBlank: true,
        formulae: [0],
      };
      worksheet.getCell(`H${rowIdx}`).dataValidation = {
        type: "list",
        allowBlank: true,
        formulae: ['"normal,odo_rusak,mt_off"'],
      };
      continue;
    }

    const lastOdoDate = formatDateCell(worksheet.getCell(`F${rowIdx}`).value);
    const keterangan = String(worksheet.getCell(`K${rowIdx}`).value || "").trim();
    const isKoreksiOdoHariIni = keterangan.includes("telah dikoreksi hari ini");
    const maxDelta = lastOdoDate
      ? getDaySpanAtLeastOne(lastOdoDate, tanggalLabel) * MAX_ODO_DELTA_PER_DAY
      : null;
    const maxDeltaRule = Number.isFinite(maxDelta)
      ? `,G${rowIdx}-E${rowIdx}<=${maxDelta}`
      : "";

    worksheet.getCell(`G${rowIdx}`).dataValidation = isKoreksiOdoHariIni
      ? {
          type: "custom",
          allowBlank: true,
          formulae: [`ISBLANK(G${rowIdx})`],
          showErrorMessage: true,
          errorTitle: "ODO tidak dapat diupdate bulk",
          error: keterangan,
        }
      : {
          type: "custom",
          allowBlank: false,
          formulae: [`AND(ISNUMBER(G${rowIdx}),G${rowIdx}>=E${rowIdx}${maxDeltaRule})`],
          showErrorMessage: true,
          errorTitle: "ODO tidak valid",
          error: Number.isFinite(maxDelta)
            ? `KM ODO wajib angka, tidak boleh lebih kecil dari Last ODO, dan kenaikan maksimal ${MAX_ODO_DELTA_PER_DAY} KM/hari.`
            : "KM ODO wajib angka dan tidak boleh lebih kecil dari Last ODO.",
        };
    worksheet.getCell(`H${rowIdx}`).dataValidation = {
      type: "list",
      allowBlank: false,
      formulae: ['"normal,odo_rusak,mt_off"'],
      showErrorMessage: true,
      errorTitle: "Jenis transaksi tidak valid",
      error: `Jenis transaksi harus salah satu: ${Object.keys(ODO_FLAG_LABELS).join(", ")}.`,
    };
    if (isKoreksiOdoHariIni) {
      worksheet.getRow(rowIdx).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFFF2CC" },
      };
      worksheet.getCell(`K${rowIdx}`).font = { color: { argb: "FF9F1239" }, bold: true };
    }
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob(
    [buffer],
    { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }
  );

  const safeTanggal = tanggalLabel;
  const safeCabang = String(namaCabang || "CABANG").trim().replace(/\s+/g, "_");
  const fileName = `FORM_BULK_ODO_${safeCabang}_${safeTanggal}.xlsx`;

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}
